import { Link, useHistory } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { connect } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import paginationFactory, {
	PaginationProvider,
	PaginationListStandalone,
	SizePerPageDropdownStandalone,
} from "react-bootstrap-table2-paginator";
import axios from "axios";
import { INTERNATIONAL_DATE_FORMAT } from "../../../../../../common/constants";
import { EMPTY_LIST } from "src/common/constants";
import { toast } from "react-toastify";
import Spinner from "react-bootstrap/Spinner";
import { Row, Col } from "reactstrap";
import {
	Dropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem
} from 'react-bootstrap';
import DateUtils from "../../../../../../services/utils/DateUtils";
import { withRouter } from "react-router-dom";
import { GDPR_REPORT_STATUS_LIST } from "src/modules/data-protection/constants/Common";
import ReportIncidenService from "../components/api/ReportIncidenService";
import highPriorityIcon from "src/modules/data-protection/assets/icons/priority-high.png";
import mediumPriorityIcon from "src/modules/data-protection/assets/icons/priority-medium.png";
import lowPriorityIcon from "src/modules/data-protection/assets/icons/priority-low.png";
import FilteringComponent from "./components/filtering-component";
import ExportExcelButton from "../components/export-button";
import FromMailboxCaseIndicator from "src/modules/data-protection/components/FromMailboxCaseIndicator";
import { getFromMailboxListQuery } from "src/modules/mailer/helpers";

const RequestTable = (props) => {
	const history = useHistory();

	const [moreActionsDropDownOpen, setMoreActionsDropDownOpen] = useState(false);
	const toggleMoreActionsDropDown = () => setMoreActionsDropDownOpen(!moreActionsDropDownOpen)

	const [tableFilters, setTableFilters] = useState({
		pageIndex: 1,
		pageSize: 25,
		status: null,
		adminCategories: null,
		risk: null,
		analystAssigned: null,
		fromMailbox: null,
		sortField: null,
		sortOrder: null,
	});

	const dateUtils = new DateUtils();

	const handleFetchCategories = useQuery({
		queryKey: ["report-request-fetch-categories"],
		queryFn: async () => {
			const service = ReportIncidenService.getInstance();

			return await service.FetchCategoriesRequest();
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: (error) => {
			toast(props.t("An error occurred while fetching categories."), {
				type: "error",
			});
		},
	});

	const handleRenderPriority = (priority) => {
		switch (priority) {
			case "HIGH": {
				return (
					<div className="priority d-flex gap-2 pt-2 text-capitalize">
						<span className="priority-icon d-flex align-items-center justify-content-center">
							<img
								src={highPriorityIcon}
								alt="Flag Icon"
								className="object-fit-contain d-inline-block mw-100"
							/>
						</span>
						{props.t(priority.toLowerCase())}
					</div>
				);
			}
			case "MEDIUM": {
				return (
					<div className="priority d-flex gap-2 pt-2  text-capitalize">
						<span className="priority-icon d-flex align-items-center justify-content-center">
							<img
								src={mediumPriorityIcon}
								alt="Flag Icon"
								className="object-fit-contain d-inline-block mw-100"
							/>
						</span>
						{props.t(priority.toLowerCase())}
					</div>
				);
			}
			case "LOW": {
				return (
					<div className="priority d-flex gap-2 pt-2  text-capitalize">
						<span className="priority-icon d-flex align-items-center justify-content-center">
							<img
								src={lowPriorityIcon}
								alt="Flag Icon"
								className="object-fit-contain d-inline-block mw-100"
							/>
						</span>
						{props.t(priority.toLowerCase())}
					</div>
				);
			}
			default: {
				return (
					<div className="d-flex gap-2 priority pt-2   text-capitalize">
						<span className="priority-icon d-flex align-items-center justify-content-center">
							<img
								src={lowPriorityIcon}
								alt="Flag Icon"
								className="object-fit-contain d-inline-block mw-100"
							/>
						</span>
						{props.t("Low")}
					</div>
				);
			}
		}
	};

	const handleRenderReportStatus = (statusId) => {
		const status = GDPR_REPORT_STATUS_LIST.find(
			(item) => item.id === parseInt(statusId)
		);
		return (
			<div
				style={{
					fontSize: "10px",
					fontWeight: 700,
					padding: "3px 8px",
					background: status.backgroundColor,
					opacity: status?.opacity || 1,
					color: "#1D1D1D",
					borderRadius: "7px",
					whiteSpace: "nowrap",
				}}
				className={"dt-task-status "}
			>
				{props.t(status.titleKey)}
			</div>
		);
	};

	const handleRenderReportCategories = (reportCategories) => {
		const categories = handleFetchCategories.data || [];
		const result = categories.length > 0
			? categories
				.filter((i) => reportCategories?.includes(i.id))
				.map((i) => props.t(i.title))
			: null;
		return result ? result.join(", ") : null;
	};

	const tableColumns = [
		{
			key: 1,
			dataField: "case_id",
			text: props.t("Request ID"),
			sort: false,
			style: {
				display: "table-cell",
				maxWidth: "200px",
			},
			headerStyle: {
				maxWidth: "200px",
			},
			formatter: (cellContent, row) => {
				return (
					<div className="d-flex align-items-center gap-2">
						<Link
							className="text-dark"
							to={`/admin/data-protection/data-subject-request/details/${row.case_id}`}
						>
							{row.secondary_case_id}
						</Link>
						<FromMailboxCaseIndicator report={row} />
					</div>
				);
			},
		},
		{
			key: 2,
			dataField: "category",
			style: {
				display: "table-cell",
				maxWidth: "200px",
			},
			sort: false,
			headerStyle: {
				maxWidth: "200px",
			},
			text: props.t("Category"),
			formatter: (cellContent, row) => {
				return <div>{handleRenderReportCategories(row?.admin_category)}</div>;
			},
		},
		{
			key: 3,
			dataField: "submission_date",
			text: props.t("Submission date"),
			sort: true,
			formatter: (cellContent, row) => {
				return (
					<div>
						{dateUtils.convertTimeStampToDate(
							cellContent,
							INTERNATIONAL_DATE_FORMAT
						)}
					</div>
				);
			},
		},
		{
			key: 4,
			dataField: "closing_date",
			text: props.t("Closing date"),
			sort: true,
			formatter: (cellContent, row) => {
				return (
					<div>
						{dateUtils.convertTimeStampToDate(
							cellContent,
							INTERNATIONAL_DATE_FORMAT
						)}
					</div>
				);
			},
		},
		{
			key: 5,
			dataField: "legal_entities",
			sort: false,
			text: props.t("Affected Legal Entities"),
			formatter: (cellContent) => {
				return (cellContent || []).map((legal) => legal.title).join(', ')
			}
		},
		{
			key: 6,
			dataField: "analyst_assigned",
			sort: true,
			text: props.t("Assigned analyst"),
			formatter: (cellContent, row) => {
				if (!cellContent || Array.isArray(cellContent)) return null;
				if (cellContent?.avatar && cellContent?.avatar !== "") {
					// avatar exists and is not an empty string
					return (
						<div className="d-flex gap-1 align-items-center">
							<div className="avatar-xs ">
								<span
									className="avatar-title bg-soft-primary text-primary rounded-circle text-capitalize"
									style={{ overflow: "hidden" }}
								>
									<img
										alt={cellContent.first_name}
										src={cellContent?.avatar}
										style={{
											width: "100%",
											height: "100%",
											display: "block",
											objectFit: "cover",
										}}
									/>
								</span>
							</div>{" "}
							{`${cellContent.first_name} ${cellContent.last_name}`}
						</div>
					);
				}
				return (
					<div>
						<span className="dt-list-col-bullet">
							{cellContent.first_name[0]}
						</span>{" "}
						{`${cellContent.first_name} ${cellContent.last_name}`}
					</div>
				);
			},
		},
		{
			dataField: "priority",
			sort: true,
			text: props.t("Priority"),
			key: 7,
			formatter: (cell, row) => {
				return <>{handleRenderPriority(row?.priority)}</>;
			},
			style: {
				display: "table-cell",
				width: "100px",
			},
			headerStyle: {
				width: "100px",
			},
		},
		{
			key: 8,
			dataField: "status",
			sort: true,
			text: props.t("Status"),
			formatter: (cellContent, row) => {
				return <div>{handleRenderReportStatus(row.status)}</div>;
			},
		},
	];

	const NoDataIndication = () =>
		handleFetchReportListQuery.isFetched &&
			!handleFetchReportListQuery.length ? (
			<div className="alert m-0" role="alert">
				<p
					style={{
						textAlign: "center",
						marginBottom: 0,
					}}
				>
					{props.t(EMPTY_LIST)}
				</p>
			</div>
		) : (
			<></>
		);

	const handleFetchReportListQuery = useQuery({
		queryKey: [
			"gdpr-fetch-data-subject-requests-list",
			tableFilters,
			props.assignedToMe
		],
		queryFn: async () => {
			const { fromMailbox, ...filters } = tableFilters;
			const mailboxQuery = getFromMailboxListQuery(fromMailbox);
			const response = await axios.post(`${props.url}`, {
				assignedToMe: props.assignedToMe,
				...filters,
				...mailboxQuery,
				questionnaire: "data_subject_request"
			}, {
				headers: {
					Authorization: `Bearer ${props.token}`,
				},
				params: mailboxQuery,
			});
			return response.data?.data || [];
		},
		cacheTime: 0,
		refetchOnWindowFocus: false,
		onError: () => {
			toast(props.t("An error occurred while fetching report list."), {
				type: "error"
			});
		}
	});

	const handleTableChange = (type, data) => {
		switch (type) {
			case "pagination":
				const { page, sizePerPage } = data;
				setTableFilters({
					...tableFilters,
					pageIndex: page,
					pageSize: sizePerPage,
				});
				break;

			case "sort": {
				const { sortField, sortOrder } = data;
				setTableFilters({
					...tableFilters,
					sortField: sortField,
					sortOrder: sortOrder,
				});
				break;
			}
			default:
				return false;
		}
	};

	const handleFilterChange = useCallback((newFilters) => {
		setTableFilters((prevFilters) => ({
			...prevFilters,
			...newFilters,
		}));
	}, []);

	return (
		<div className="p-4" style={{ marginBottom: "300px" }}>
			<PaginationProvider
				pagination={paginationFactory({
					custom: true,
					page: tableFilters.pageIndex,
					sizePerPage: tableFilters.pageSize,
					totalSize: handleFetchReportListQuery.data?.itemsCount || 0,
					withFirstAndLast: false,
					alwaysShowAllBtns: true,
					prePageText: (
						<span>
							<i className="ri-arrow-left-s-line"></i> {props.t("Back")}
						</span>
					),
					nextPageText: (
						<span>
							{props.t("Next")} <i className="ri-arrow-right-s-line"></i>
						</span>
					),
					prePageTitle: props.t("Pre page"),
					firstPageTitle: props.t("Next page"),
					showTotal: true,
					hideSizePerPage: false,
					sizePerPageList: [
						{
							text: "25",
							value: 25,
						},
						{
							text: "50",
							value: 50,
						},
					],
				})}
			>
				{({ paginationProps, paginationTableProps }) => {
					return (
						<React.Fragment>
							<Row className="mb-5 mt-3">
								<Col sm="12">
									<div className="d-flex gap-2 justify-content-end me-2">
										<FilteringComponent
											onChange={handleFilterChange}
											categories={handleFetchCategories}
										/>
										<Dropdown
											isOpen={moreActionsDropDownOpen}
											toggle={toggleMoreActionsDropDown}
										>
											<DropdownToggle
												caret
												variant="outline-secondary"
												outline
											>
												{props.t('Actions')}
											</DropdownToggle>
											<DropdownMenu
												className="dropdown-menu-end"
											>
												<DropdownItem
													onClick={() => {
														history.push('/admin/data-protection/data-subject-request/create')
													}}
												>
													<i className="ri-add-line align-middle me-2" />
													{props.t("Add A New Request")}
												</DropdownItem>

												<DropdownItem
													onClick={(e) => {
														e.preventDefault()
														e.stopPropagation()
													}}
												>
													<ExportExcelButton
														filters={tableFilters}
													/>
												</DropdownItem>
											</DropdownMenu>
										</Dropdown>
									</div>
								</Col>
							</Row>
							<Row>
								<Col sm="12">
									<BootstrapTable
										remote={{
											pagination: true,
											filter: false,
											sort: true,
											cellEdit: false,
											search: false,
										}}
										loading={
											handleFetchReportListQuery.isFetching ||
											handleFetchReportListQuery.isLoading ||
											handleFetchCategories.isFetching
										}
										overlay={overlayFactory({
											spinner: (
												<Spinner
													animation="border"
													variant="primary"
													size="md"
												/>
											),
											text: "Loading...",
										})}
										onTableChange={handleTableChange}
										defaultSorted={[]}
										keyField={"id"}
										responsive
										bordered={false}
										data={handleFetchReportListQuery.data?.reports || []}
										striped={true}
										columns={tableColumns}
										wrapperClasses="table-responsive"
										classes={"table tpdd-table incident-table"}
										headerWrapperClasses={"thead-light"}
										style={{
											overflowX: "auto",
										}}
										noDataIndication={() => <NoDataIndication />}
										{...paginationTableProps}
									/>
								</Col>
							</Row>

							<Row>
								<Col sm="12" md="6">
									<div className="tpdd-pagination-style-1">
										<PaginationListStandalone {...paginationProps} />

										<SizePerPageDropdownStandalone {...paginationProps} />
									</div>
								</Col>
							</Row>
						</React.Fragment>
					);
				}}
			</PaginationProvider>
		</div>
	);
};

const mapStatetoProps = (state) => {
	const { token } = state.Login;
	return {
		token,
	};
};

export default withNamespaces()(
	withRouter(connect(mapStatetoProps)(RequestTable))
);
